import React, { useState } from 'react';

import { Grid, Typography, Card, CardContent, ClickAwayListener, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { usePreview } from '../../../utils/hooks';
import { GraphPreview } from 'src/App/Components';
import { ViewDetailsButton } from 'src/App/Components/Buttons';
import { DynamicBarChart, DynamicPieChart } from 'src/App/Components/Charts';
import { InfoTooltip } from '../Tooltips';
import {
  COLOR_PRIMARY,
  COLOR_TOOLTIP_BACKGROUND,
  dummyPropertySizeBarGraphData,
  dummyPieChartData,
  COLOR_E,
} from 'src/const';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_600,
  COLOR_SECONDARY_2_600,
  COLOR_TEXT_2,
  COLOR_PRIMARY_2,
  COLOR_TERTIARY_1_300,
  COLOR_PRIMARY_1_800,
  COLOR_PRIMARY_1_700,
  COLOR_PRIMARY_1_500,
  COLOR_PRIMARY_1_400,
  COLOR_TERTIARY_1_400,
  COLOR_TERTIARY_1_500,
  COLOR_TERTIARY_1_200,
  COLOR_TERTIARY_1_100,
  COLOR_SECONDARY_2,
} from 'src/colors';
import {
  getDemandByPropertySize,
  getDemandByPropertyType,
  getDemandByChannel,
  getHeadingStyle,
  getPreviewTextObj,
} from 'src/utils/functions';
import { infoIconStyle, cardTitle, infoIconButton } from 'src/App/Dashboards/Market/styles/style';
import { DemandCardPropTypes } from 'src/interfaces/iproptypes';
import UnlockButton from '../CTA/UnlockButton';

const DemandCard = ({
  listings,
  otaListings,
  handleOnSampleClick,
  isDisabled,
  filterVal,
}: DemandCardPropTypes) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const [isPropertySizePreview, handlePropertySizePreviewMode] = usePreview() as [
    boolean,
    () => void
  ];
  const [
    isPropertyTypeAndChannelPreview,
    handlePropertyTypeAndChannelPreviewMode,
  ] = usePreview() as [boolean, () => void];

  const propertySizePreviewTextObj = getPreviewTextObj(
    'demand',
    'propertySize',
    filterVal,
    listings.data
  );
  const propertyTypePreviewTextObj = getPreviewTextObj(
    'demand',
    'propertyType',
    filterVal,
    listings.data
  );
  const channelPreviewTextObj = getPreviewTextObj(
    'demand',
    'channel',
    filterVal,
    listings.data,
    // otaListings.data
  );

  const demandByPropertySize = getDemandByPropertySize(listings.data);
  const demandByPropertyType = getDemandByPropertyType(listings.data);
  // const demandByChannel = getDemandByChannel(listings.data, otaListings.data);
  const demandByChannel = getDemandByChannel(listings.data, []);

  return (
    <>
      <Card style={{ width: '100%' }}>
        <CardContent style={{ position: 'relative' }}>
          <Grid
            container
            direction="column"
            justifyContent="space-between"
            alignItems="flex-start"
            item
            sx={{ position: 'relative', top: '0', left: '0' }}
          >
            <Typography variant="h2" sx={cardTitle}>
              Revenue Breakdown
              <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
                <InfoTooltip
                  title={
                    <Card>
                      <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                        <Grid container direction="column" alignItems="flex-start">
                          <Typography variant="h6" sx={{ fontWeight: 'bolder' }}>
                            Revenue Breakdown
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            The double pie chart and the three segmented bars show the breakdown of
                            revenue by:
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <span style={{ fontWeight: 900, textDecoration: 'underline' }}>
                              {'Channel (the inner pie): '}
                            </span>{' '}
                            Airbnb, VRBO, Booking.com, Tripadvisor
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <span style={{ fontWeight: 900, textDecoration: 'underline' }}>
                              {'Property Type (the outer pie) (only Airbnb listings): '}
                            </span>{' '}
                            Entire Home/Apartment, Condo, Shared Room, Hotel Room, Private Room
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <span style={{ fontWeight: 900, textDecoration: 'underline' }}>
                              {'Property Size (only Airbnb listings): '}
                            </span>{' '}
                            Studio, 1 to 6+ bedrooms
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <span style={{ fontWeight: 900, textDecoration: 'underline' }}>
                              {'Booking Type (only Airbnb listings): '}
                            </span>{' '}
                            Single Night, Multiple Nights, Weekly, Mid-term
                          </Typography>
                          <br />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <span style={{ fontWeight: 900, textDecoration: 'underline' }}>
                              {'Booking Lead Time (only Airbnb listings): '}
                            </span>{' '}
                            Last-Minute, Standard, Advanced
                          </Typography>
                        </Grid>
                      </CardContent>
                    </Card>
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                  open={isTooltipOpen}
                  onOpen={() => setIsTooltipOpen(true)}
                  onClose={() => setIsTooltipOpen(false)}
                >
                  <IconButton
                    style={infoIconButton}
                    onClick={() => setIsTooltipOpen(prev => !prev)}
                  >
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              </ClickAwayListener>
            </Typography>

            <Grid
              container
              direction="column"
              justifyContent="space-between"
              item
              xs={12}
              spacing={2}
              sx={{
                marginTop: '5px',
                // filter: isDisabled ? 'blur(4px)' : 'none',
                // pointerEvents: isDisabled ? 'none' : 'auto',
              }}
            >
              <Grid container item sx={{ width: '100%' }}>
                <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                  Property Size
                  <InfoTooltip
                    enterTouchDelay={0}
                    leaveTouchDelay={30000}
                    title={
                      <Card>
                        <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            This data only includes entire place listings on Airbnb.
                          </Typography>
                        </CardContent>
                      </Card>
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <IconButton
                      style={infoIconButton}
                      onClick={() => setIsTooltipOpen(prev => !prev)}
                    >
                      <InfoOutlinedIcon style={infoIconStyle} />
                    </IconButton>
                  </InfoTooltip>
                </Typography>
                {isPropertySizePreview ? (
                  <GraphPreview
                    parent="demand"
                    variant="propertySize"
                    useFriendlyDataName={
                      isDisabled ? '1 Bedroom' : propertySizePreviewTextObj.userFriendlyDataName
                    }
                    previewVal={propertySizePreviewTextObj.previewVal}
                    isDisabled={isDisabled}
                  />
                ) : (
                  <Grid item container sx={{ position: 'relative' }}>
                    <DynamicBarChart
                      layout="horizontal"
                      data={isDisabled ? dummyPropertySizeBarGraphData : demandByPropertySize}
                      unit="%"
                      label="Bedrooms"
                      height={150}
                      barColor={COLOR_SECONDARY_2_600}
                      barAccentColor={COLOR_SECONDARY_2}
                      useShortHand={true}
                    />
                    {isDisabled && (
                      <UnlockButton
                        fontSizeHeading="small"
                        handleOnSampleClick={handleOnSampleClick}
                        noBackdrop={true}
                      />
                    )}
                  </Grid>
                )}
                <ViewDetailsButton
                  color={COLOR_PRIMARY_2}
                  isPreview={isPropertySizePreview}
                  handlePreviewMode={
                    isDisabled ? handleOnSampleClick : handlePropertySizePreviewMode
                  }
                />
              </Grid>

              {isPropertyTypeAndChannelPreview ? (
                <>
                  <Grid item>
                    <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                      Property Type
                      <InfoTooltip
                        title={
                          <Card>
                            <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                              <Grid container direction="column" alignItems="flex-start">
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                  Property Type and STR platform distribution
                                </Typography>
                              </Grid>
                            </CardContent>
                          </Card>
                        }
                        enterTouchDelay={0}
                        leaveTouchDelay={10000}
                      >
                        <IconButton style={infoIconButton}>
                          <InfoOutlinedIcon style={infoIconStyle} />
                        </IconButton>
                      </InfoTooltip>
                    </Typography>
                    <GraphPreview
                      parent="demand"
                      variant="propertyType"
                      useFriendlyDataName={
                        isDisabled ? 'Entire Home' : propertyTypePreviewTextObj.userFriendlyDataName
                      }
                      previewVal={propertyTypePreviewTextObj.previewVal}
                      isDisabled={isDisabled}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                      Channel Type
                    </Typography>
                    <GraphPreview
                      parent="demand"
                      variant="channel"
                      useFriendlyDataName={channelPreviewTextObj.userFriendlyDataName}
                      previewVal={channelPreviewTextObj.previewVal}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item>
                  <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                    Property Type and Channel
                    <InfoTooltip
                      title={
                        <Card>
                          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                            <Grid container direction="column" alignItems="flex-start">
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                Property Type and STR platform distribution
                              </Typography>
                            </Grid>
                          </CardContent>
                        </Card>
                      }
                      enterTouchDelay={0}
                      leaveTouchDelay={10000}
                    >
                      <IconButton style={infoIconButton}>
                        <InfoOutlinedIcon style={infoIconStyle} />
                      </IconButton>
                    </InfoTooltip>
                  </Typography>

                  <Grid
                    xs={12}
                    item
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ position: 'relative' }}
                  >
                    <Grid item xs={6} >
                      <DynamicPieChart
                        data={isDisabled ? dummyPieChartData : demandByPropertyType}
                        unit={'%'}
                        pieColors={[
                          COLOR_PRIMARY_1_800,
                          COLOR_PRIMARY_1_700,
                          COLOR_PRIMARY_1_600,
                          COLOR_PRIMARY_1_500,
                          COLOR_PRIMARY_1_400,
                        ]}
                        pieAccentColor={COLOR_PRIMARY_1}
                        label={'Property Type'}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DynamicPieChart
                        data={demandByChannel}
                        unit={'%'}
                        pieColors={[
                          COLOR_TERTIARY_1_400,
                          COLOR_TERTIARY_1_300,
                          COLOR_TERTIARY_1_200,
                          COLOR_TERTIARY_1_100,
                        ]}
                        pieAccentColor={COLOR_TERTIARY_1_500}
                        label={'Channel'}
                      />
                    </Grid>
                    {isDisabled && (
                    <UnlockButton
                      fontSizeHeading="small"
                      handleOnSampleClick={handleOnSampleClick}
                      noBackdrop={true}
                    />
                  )}
                  </Grid>
                </Grid>
              )}
              <Grid item>
                <ViewDetailsButton
                  color={COLOR_PRIMARY}
                  isPreview={isPropertyTypeAndChannelPreview}
                  handlePreviewMode={
                    isDisabled ? handleOnSampleClick : handlePropertyTypeAndChannelPreviewMode
                  }
                />
              </Grid>
            </Grid>

            {/* {isDisabled && (
              <Typography
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  fontSize: '34px',
                  fontWeight: 900,
                  color: COLOR_E,
                  transform: 'translate(-50%, -50%) rotate(-30deg)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  handleOnSampleClick();
                }}
              >
                SAMPLE
              </Typography>
            )} */}
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

// export default DemandCard;

export default React.memo(DemandCard);
